import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api/client'
import type { components } from '@/lib/api/generated'

type POResponse = components['schemas']['POResponse']
type Scan = { barcode: string; variant_id?: number | null; qty?: number }

export function useReceiveScan(poId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scan: Scan) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/receive',
        {
          params: { path: { po_id: poId } },
          body: {
            scans: [
              {
                barcode: scan.barcode,
                variant_id: scan.variant_id ?? null,
                qty: scan.qty ?? 1,
              },
            ],
          },
        }
      )
      if (error) throw new Error('Receive failed')
      return data!.results[0]
    },
    onMutate: async (scan) => {
      await qc.cancelQueries({ queryKey: ['po', poId] })
      const prev = qc.getQueryData<POResponse>(['po', poId])
      qc.setQueryData<POResponse>(['po', poId], (old) => {
        if (!old) return old
        const items = (old.items ?? []).map((it) => {
          const matches =
            (scan.variant_id != null && it.variant_id === scan.variant_id) ||
            (!!scan.barcode && it.barcode === scan.barcode)
          if (!matches) return it
          return { ...it, qty_received: it.qty_received + (scan.qty ?? 1) }
        })
        return { ...old, items }
      })
      return { prev }
    },
    onSuccess: (result, scan, ctx) => {
      if (!result.matched) {
        // Roll back optimistic update — barcode not found in this PO
        if (ctx?.prev) qc.setQueryData<POResponse>(['po', poId], ctx.prev)
        return
      }
      // Look up the product title from the (optimistically updated) cache
      const po = qc.getQueryData<POResponse>(['po', poId])
      const item = po?.items?.find(
        (it) =>
          (scan.variant_id != null && it.variant_id === scan.variant_id) ||
          it.barcode === scan.barcode
      )
      const label = item
        ? [item.product_title, item.variant_title].filter(Boolean).join(' · ')
        : scan.barcode
      // Show "+N" for receipts, "−N" for corrections. Bare "+result.qty"
      // produces "+-1" when the user clicks the subtract button.
      const sign = result.qty < 0 ? '−' : '+'
      const magnitude = Math.abs(result.qty)
      toast.success(`${sign}${magnitude} ${label}`, { duration: 1000 })
    },
    onError: (_err, _scan, ctx) => {
      if (ctx?.prev) qc.setQueryData<POResponse>(['po', poId], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['po', poId] })
    },
  })
}

// Receive every still-open line item at once: one /receive call carrying a
// scan per item with qty = the remaining (qty_ordered − qty_received). The
// caller builds the scan list (it knows which items have a barcode/variant_id
// to match on). Returns the endpoint's results array.
export function useReceiveAll(poId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scans: Scan[]) => {
      if (scans.length === 0) return { results: [] }
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/receive',
        {
          params: { path: { po_id: poId } },
          body: {
            scans: scans.map((s) => ({
              barcode: s.barcode,
              variant_id: s.variant_id ?? null,
              qty: s.qty ?? 1,
            })),
          },
        }
      )
      if (error) throw new Error('Receive all failed')
      return data!
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['po', poId] })
      qc.invalidateQueries({ queryKey: ['pos'] })
    },
  })
}
